'use client';

import { faBrowser, faCalendarDays, faTimes } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { Partner } from '@medprev-web-app/api-graphql-client';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Modal } from '@/components/ui/modal.component';
import { Link } from '@/components/ui/link/link.component';
import { useAgendamentoNavigation } from '@/hooks/navigation/use-agendamento-navigation.hook';
import { resolvePartnerName } from '@/utils/partner/partner.util';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { TrackUserView } from '@/app/(main)/_components/track-user-view/track-user-view.component';

interface PartnerRedirectModalProps {
  open: boolean;
  partner?: Partner | null;
  onOpenChange: (isOpen: boolean) => void;
}

export function PartnerRedirectModal({ open, partner, onOpenChange }: PartnerRedirectModalProps) {
  const { buildLink } = useAgendamentoNavigation();

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <Modal.Content
        className="px-3 py-5"
        slotHeader={(
          <div className="flex justify-between gap-2 pb-0 items-start w-full">
            <div className="flex items-start gap-2">
              <Icon icon={faBrowser} size="xl" className="text-green-400 mt-1" />
              <span
                data-testid={DataTestIdEnum.PARTNER_REDIRECT_MODAL_TITLE}
                className="font-bold text-2xl text-gray-900 text-left"
              >
                Aviso de redirecionamento
              </span>
            </div>
            <Modal.Close
              asChild
              className="text-gray-500 cursor-pointer self-start px-2 hover:bg-gray-50"
              data-analytics-event={AnalyticsEnum.CLICK_PARTNER_MODAL_CANCEL_REDIRECT}
            >
              <Icon
                data-testid={DataTestIdEnum.PARTNER_REDIRECT_MODAL_CLOSE_BUTTON}
                icon={faTimes}
                size="2xl"
                className="hover:text-gray-900"
              />
            </Modal.Close>
          </div>
        )}
      >
        <div className="mt-4 px-2">
          <TrackUserView eventName={AnalyticsEnum.VIEW_PARTNER_MODAL_REDIRECT} />
          <p className="text-gray-800" data-testid={DataTestIdEnum.PARTNER_REDIRECT_MODAL_TEXT}>
            Você será redirecionado para o site de agendamentos online da Medprev, onde você pode ver todos os serviços
            com
            {' '}
            {resolvePartnerName(partner as Partner)}
            {' '}
            e agendar um atendimento. É rápido e seguro. Clique no botão abaixo para
            continuar:
          </p>

          <Button
            data-testid={DataTestIdEnum.PARTNER_REDIRECT_MODAL_LINK_REDIRECT}
            asChild
            startIcon={faCalendarDays}
            className="mt-6"
          >
            <Link
              prefetch={false}
              href={buildLink({ path: `/parceiros/${partner?.id}/` })}
              target="_blank"
              rel="noopener noreferrer"
              data-analytics-event={AnalyticsEnum.CLICK_PARTNER_CONFIRM_REDIRECT}
            >
              Ver serviços e horários
            </Link>
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  );
}
